"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import type { RemoveBgHistoryItem, RemoveBgJob } from "@/types/removebg";
import { getBackgroundRemovalHistory } from "@/services/backgroundRemoval.service";
import { useUser } from "@/context/UserContext";

interface RemoveBgHistoryProps {
  onSelectJob: (job: RemoveBgJob) => void;
  activeJobId: string | null;
  refreshKey: number;
}

const formatter = new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "short",
  timeStyle: "short"
});

function historyItemToJob(item: RemoveBgHistoryItem): RemoveBgJob {
  return {
    status: "success",
    message: item.message ?? "Fundo removido com sucesso.",
    job_id: item.job_id,
    processing_time_ms: item.processing_time_ms,
    url_preview: item.url_preview,
    url_hd: item.url_hd,
    url_original: item.url_original,
    created_at: item.created_at,
    expires_at: item.expires_at,
    original:
      item.original ?? {
        filename: "imagem-processada.png",
        size_bytes: 0,
        mimetype: "image/png"
      }
  };
}

export default function RemoveBgHistory({ onSelectJob, activeJobId, refreshKey }: RemoveBgHistoryProps) {
  const { user } = useUser();
  const isAuthenticated = Boolean(user);

  const [items, setItems] = useState<RemoveBgHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const history = await getBackgroundRemovalHistory(20, isAuthenticated);
      setItems(history);
    } catch (historyError) {
      console.warn("Erro ao carregar histórico", historyError);
      setError("Não foi possível carregar o histórico recente.");
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory, refreshKey]);

  const hasItems = useMemo(() => items.length > 0, [items]);

  const handleSelect = useCallback(
    (item: RemoveBgHistoryItem) => {
      onSelectJob(historyItemToJob(item));
    },
    [onSelectJob]
  );

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-white">Histórico recente</h2>
          <p className="text-sm text-white/60">Acesse novamente os últimos arquivos processados.</p>
        </div>
        {isLoading && <span className="text-sm text-white/50">Sincronizando...</span>}
      </div>

      {error && <p className="text-sm font-medium text-amber-300">{error}</p>}

      <div className="overflow-x-auto">
        <div className="flex min-h-[140px] gap-4">
          {isLoading && !hasItems && (
            <div className="flex w-full items-center justify-center rounded-2xl border border-white/10 bg-white/5 p-8 text-white/50">
              Carregando histórico...
            </div>
          )}

          {!isLoading && !hasItems && !error && (
            <div className="flex w-full items-center justify-center rounded-2xl border border-white/10 bg-white/5 p-8 text-white/50">
              Nenhum processamento recente encontrado.
            </div>
          )}

          {items.map((item) => {
            const isActive = activeJobId === item.job_id;
            const timestamp = item.created_at ?? item.expires_at ?? new Date().toISOString();
            const displayUrl = item.url_hd ?? item.url_preview;
            return (
              <button
                key={item.job_id}
                type="button"
                onClick={() => handleSelect(item)}
                className={clsx(
                  "flex min-w-[200px] flex-col gap-3 rounded-2xl border px-4 py-4 text-left transition",
                  isActive ? "border-brand-secondary bg-brand-secondary/20" : "border-white/10 bg-white/5 hover:border-brand-secondary/40"
                )}
              >
                {displayUrl && (
                  <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-white/5">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={displayUrl}
                      alt="Imagem processada"
                      className="absolute inset-0 h-full w-full object-cover"
                      loading="lazy"
                      style={{
                        backgroundImage: 'url(/images/backgrounds/fundocriedesign.jpg)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      }}
                    />
                  </div>
                )}
                <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-white/50">
                  <span>ID {item.job_id.slice(-6)}</span>
                  <span>{formatter.format(new Date(timestamp))}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
